/**
 * Dashboard view — customizable widget registry (v1, 2026-07-10).
 *
 * Every section is a named widget. Users choose which widgets show, their
 * order, and (where sensible) full vs half width via the Customize modal;
 * the layout persists in Settings.dashboardLayout (cloud-synced through
 * user_prefs, so it follows the user across devices). An empty stored layout
 * means "registry defaults", which mirror the original fixed dashboard.
 *
 * "Expenses YTD" / "Net YTD" are TRUE year-to-date as of 2026-07-10 (they
 * previously summed all-time). "Realized P/L (Sales)" stays all-time by
 * design. Charts read theme CSS variables so light mode renders correctly.
 */
import Chart from "chart.js/auto";
import { icon } from "../utils/icon";
import { DB } from "../lib/db";
import { escapeHtml } from "../lib/dom";
import { fmt$, fmt$0, fmtN, fmtDate, sum, kpi, snap } from "../utils/format";
import { isBulkPoolRow } from "../lib/bulk";
import { ownerLoanBalance, ownerLoanContributions, ownerLoanRepayments, cashFlowBreakdown } from "../lib/derived";
import { booksHealthSummary } from "../lib/booksHealth";
import { Settings } from "../lib/prefs";
import { openModal } from "../components/modal";
import { render } from "../state";
import type { Row } from "../lib/types";

/* ------------------------------------------------------------------ */
/*  Shared per-render context — computed once, consumed by widgets      */
/* ------------------------------------------------------------------ */
interface DashCtx {
  sealed: Row[]; cards: Row[]; sales: Row[]; expenses: Row[]; mileage: Row[];
  sealedBasis: number; sealedFMV: number; cardsBasis: number; cardsFMV: number;
  totalBasis: number; totalFMV: number; unrealized: number;
  realized: number; realizedYtd: number; expYtd: number; expTotal: number;
  milesYtd: number; milesDeductionYtd: number;
  sealedQty: number; cardsQty: number;
  sortedTypes: Array<[string, number]>;
  expensesSorted: Array<[string, number]>;
  trendLabels: string[]; trendValues: number[]; trendTotal: number; trendBest: number; trendBestIdx: number;
  topCards: Row[];
  recentItems: Array<{ _kind: string; _id: string; name: string; sub: string; acq: string; qty: number; fmv: number; img: string }>;
  cf: ReturnType<typeof cashFlowBreakdown>;
  health: ReturnType<typeof booksHealthSummary>;
  ownerContrib: number; ownerBal: number; ownerRepaid: number;
  year: string;
}

function computeCtx(): DashCtx {
  const sealed   = DB.table("sealed");
  const cards    = DB.table("cards").filter(r => !isBulkPoolRow(r));
  const sales    = DB.table("sales").filter(s => String(s["Status"] || "Completed") === "Completed");
  const expenses = DB.table("expenses");
  const mileage  = DB.table("mileage");

  const now = new Date();
  const year = String(now.getFullYear());
  const inYear = (d: any) => String(d || "").startsWith(year);

  const sealedBasis = sum(sealed,   r => r["Total Basis"]);
  const sealedFMV   = sum(sealed,   r => r["Current FMV"]);
  const cardsBasis  = sum(cards,    r => r["Total Basis"]);
  const cardsFMV    = sum(cards,    r => r["Current FMV"]);

  // YTD figures are filtered to the current calendar year; all-time versions
  // keep their original meaning where the label doesn't claim YTD.
  const realized    = sum(sales, r => r["Net Profit"]);
  const realizedYtd = sum(sales.filter(r => inYear(r["Date"])), r => r["Net Profit"]);
  const expTotal    = sum(expenses, r => r["Amount"]);
  const expYtd      = sum(expenses.filter(r => inYear(r["Date"])), r => r["Amount"]);
  const mileageYtdRows = mileage.filter(r => inYear(r["Date"]));
  const milesYtd          = sum(mileageYtdRows, r => r["Miles"]);
  const milesDeductionYtd = sum(mileageYtdRows, r => r["Deduction"]);

  const byProductType: Record<string, number> = {};
  for (const r of sealed) {
    const k = r["Product Type"] || "Other";
    byProductType[k] = (byProductType[k] || 0) + (Number(r["Total Basis"]) || 0);
  }
  const sortedTypes = Object.entries(byProductType).sort((a, b) => b[1] - a[1]);

  const expenseByCat: Record<string, number> = {};
  for (const r of expenses) {
    const k = r["Category"] || "Other";
    expenseByCat[k] = (expenseByCat[k] || 0) + (Number(r["Amount"]) || 0);
  }
  const expensesSorted = Object.entries(expenseByCat).sort((a, b) => b[1] - a[1]);

  // Monthly sales trend — last 12 months, including months with zero sales.
  const trendLabels: string[] = [];
  const trendValues: number[] = [];
  const monthKey = (y: number, m: number) => `${y}-${String(m + 1).padStart(2, "0")}`;
  const byMonth = new Map<string, number>();
  for (let i = 11; i >= 0; i--) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    const k = monthKey(d.getFullYear(), d.getMonth());
    byMonth.set(k, 0);
    trendLabels.push(d.toLocaleDateString(undefined, { month: "short", year: "2-digit" }));
  }
  for (const r of sales) {
    const k = String(r["Date"] || "").slice(0, 7);
    if (byMonth.has(k)) byMonth.set(k, (byMonth.get(k) || 0) + (Number(r["Net Profit"]) || 0));
  }
  for (const k of byMonth.keys()) trendValues.push(+(byMonth.get(k) || 0).toFixed(2));
  const trendTotal = trendValues.reduce((s, v) => s + v, 0);
  const trendBest  = trendValues.length ? Math.max(...trendValues) : 0;
  const trendBestIdx = trendValues.indexOf(trendBest);

  const topCards = cards.slice()
    .sort((a, b) => (Number(b["Current FMV"]) || 0) - (Number(a["Current FMV"]) || 0))
    .slice(0, 8);

  const recentItems = [
    ...cards.map(r => ({
      _kind: "Card",
      _id: String(r["Card Lot ID"] || ""),
      name: String(r["Card Name"] || ""),
      sub:  [r["Set"], r["Card #"] && `#${r["Card #"]}`, r["Condition/Grade"]].filter(Boolean).join(" \u00b7 "),
      acq: String(r["Acq Date"] || ""),
      qty: Number(r["Qty"]) || 0,
      fmv: Number(r["Current FMV"]) || 0,
      img: String(r["Image URL"] || ""),
    })),
    ...sealed.map(r => ({
      _kind: "Sealed",
      _id: String(r["Lot ID"] || ""),
      name: String(r["Product"] || ""),
      sub:  [r["Set"], r["Product Type"]].filter(Boolean).join(" \u00b7 "),
      acq: String(r["Acq Date"] || ""),
      qty: Number(r["Qty"]) || 0,
      fmv: Number(r["Current FMV"]) || 0,
      img: String(r["Image URL"] || ""),
    })),
  ].sort((a, b) => String(b.acq).localeCompare(String(a.acq))).slice(0, 10);

  return {
    sealed, cards, sales, expenses, mileage,
    sealedBasis, sealedFMV, cardsBasis, cardsFMV,
    totalBasis: sealedBasis + cardsBasis,
    totalFMV: sealedFMV + cardsFMV,
    unrealized: (sealedFMV + cardsFMV) - (sealedBasis + cardsBasis),
    realized, realizedYtd, expYtd, expTotal,
    milesYtd, milesDeductionYtd,
    sealedQty: sum(sealed, r => r["Qty"]),
    cardsQty:  sum(cards,  r => r["Qty"]),
    sortedTypes, expensesSorted,
    trendLabels, trendValues, trendTotal, trendBest, trendBestIdx,
    topCards, recentItems,
    cf: cashFlowBreakdown(),
    health: booksHealthSummary(),
    ownerContrib: ownerLoanContributions(), ownerBal: ownerLoanBalance(), ownerRepaid: ownerLoanRepayments(),
    year,
  };
}

/** Chart colors from the live theme so light mode renders correctly. */
function chartTheme() {
  const css = getComputedStyle(document.documentElement);
  return {
    muted: css.getPropertyValue("--muted").trim() || "#93a3b8",
    text:  css.getPropertyValue("--text").trim()  || "#e6ecf5",
    grid:  (css.getPropertyValue("--border").trim() || "#243049") + "66",
  };
}

/* ------------------------------------------------------------------ */
/*  Widget registry                                                     */
/* ------------------------------------------------------------------ */
interface DashWidget {
  key: string;
  label: string;
  /** Rendered at half width by default (lg screens; always full on mobile). */
  defaultHalf?: boolean;
  /** Width toggle offered in the Customize modal. */
  canHalf?: boolean;
  render: (c: DashCtx) => string;
  after?: (c: DashCtx) => void;
}

const WIDGETS: DashWidget[] = [
  {
    key: "hero", label: "Total Inventory header",
    render: c => `
      <div class="panel p-4 flex flex-wrap items-baseline gap-x-4 gap-y-1">
        <span class="text-xs text-[var(--muted)] uppercase tracking-wider">Total Inventory</span>
        <span class="stat-num text-2xl sm:text-3xl font-bold leading-none">${fmt$0(c.totalFMV)}</span>
        <span class="text-xs text-[var(--muted)] ml-auto">Unrealized <b class="stat-num ${c.unrealized >= 0 ? "text-emerald-300" : "text-rose-300"}">${c.unrealized >= 0 ? "+" : ""}${fmt$0(c.unrealized)}</b></span>
      </div>`,
  },
  {
    key: "kpis", label: "KPI tiles",
    render: c => `
      <div class="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
        ${kpi("Sealed FMV",           fmt$0(c.sealedFMV))}
        ${kpi("Cards FMV",            fmt$0(c.cardsFMV))}
        ${kpi("Realized P/L (Sales)", fmt$0(c.realized),                  c.realized                >= 0 ? "green" : "red")}
        ${kpi("Expenses YTD",         fmt$0(c.expYtd))}
        ${kpi("Net YTD",              fmt$0(c.realizedYtd - c.expYtd),    (c.realizedYtd - c.expYtd) >= 0 ? "green" : "red")}
      </div>`,
  },
  {
    key: "owner", label: "Owed to Owner",
    render: c => c.ownerContrib > 0.005 ? `
      <div class="panel p-3 flex flex-wrap items-center gap-x-6 gap-y-1 text-sm">
        <span class="text-[var(--muted)] uppercase tracking-wider text-xs">Owed to Owner</span>
        <b class="stat-num text-lg ${c.ownerBal > 0.005 ? 'text-amber-300' : 'text-emerald-300'}">${fmt$(c.ownerBal)}</b>
        <span class="text-xs text-[var(--muted)]">contributed ${fmt$(c.ownerContrib)} \u00b7 repaid ${fmt$(c.ownerRepaid)}</span>
      </div>` : "",
  },
  {
    key: "cash", label: "Cash on Hand", canHalf: true,
    render: c => `
      <div class="panel p-4">
        <div class="text-xs text-[var(--muted)] uppercase tracking-wider mb-1">Cash on Hand</div>
        <div class="stat-num text-3xl font-bold mb-3 ${c.cf.onHand >= 0 ? 'text-emerald-300' : 'text-rose-300'}">${fmt$(c.cf.onHand)}</div>
        <div class="grid grid-cols-2 sm:grid-cols-4 gap-2 text-xs text-[var(--muted)]">
          <div>Starting<b class="block text-[var(--text)] stat-num">${fmt$(c.cf.starting)}</b></div>
          <div>Sales in<b class="block text-emerald-300 stat-num">+${fmt$(c.cf.salesIn)}</b></div>
          <div>Purchases<b class="block text-rose-300 stat-num">\u2212${fmt$(c.cf.purchasesOut)}</b></div>
          <div>Expenses<b class="block text-rose-300 stat-num">\u2212${fmt$(c.cf.expensesOut)}</b></div>
          <div>Grading<b class="block text-rose-300 stat-num">\u2212${fmt$(c.cf.gradingOut)}</b></div>
          <div>Trade cash<b class="block stat-num ${c.cf.tradesNet >= 0 ? 'text-emerald-300' : 'text-rose-300'}">${c.cf.tradesNet >= 0 ? '+' : '\u2212'}${fmt$(Math.abs(c.cf.tradesNet))}</b></div>
          <div>Owner cash<b class="block stat-num ${c.cf.adjustmentsNet >= 0 ? 'text-emerald-300' : 'text-rose-300'}">${c.cf.adjustmentsNet >= 0 ? '+' : '\u2212'}${fmt$(Math.abs(c.cf.adjustmentsNet))}</b></div>
        </div>
        <div class="text-xs text-[var(--muted)] mt-2">Set your opening balance in Configs \u2192 Defaults. Mileage and box rips aren't cash events, so they're excluded.</div>
      </div>`,
  },
  {
    key: "health", label: "Books health",
    render: c => `
      <div class="panel p-3 flex items-center gap-3 text-sm cursor-pointer" onclick="openBooksHealth()" title="Run the full books health check">
        ${c.health.errors ? `<span class="text-rose-300 font-semibold inline-flex items-center gap-1">${icon("alert-circle")} ${c.health.errors} error${c.health.errors === 1 ? "" : "s"}</span>` : ""}
        ${c.health.warns ? `<span class="text-amber-300 inline-flex items-center gap-1">${icon("alert-triangle")} ${c.health.warns} warning${c.health.warns === 1 ? "" : "s"}</span>` : ""}
        ${(!c.health.errors && !c.health.warns) ? `<span class="text-emerald-300 font-semibold inline-flex items-center gap-1">${icon("check-circle")} Books health: all clear</span>` : ""}
        <span class="text-xs text-[var(--muted)] ml-auto">Open books health check \u2192</span>
      </div>`,
  },
  {
    key: "productType", label: "Sealed by Product Type (chart)", defaultHalf: true, canHalf: true,
    render: () => `
      <div class="panel p-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="font-semibold">Sealed by Product Type</h3>
          <span class="text-xs text-[var(--muted)]">Cost basis</span>
        </div>
        <canvas id="chartProductType" height="170"></canvas>
      </div>`,
    after: c => {
      if (!c.sortedTypes.length) return;
      const el = document.getElementById("chartProductType") as HTMLCanvasElement | null;
      if (!el) return;
      const th = chartTheme();
      new Chart(el, {
        type: "bar",
        data: {
          labels: c.sortedTypes.map(x => x[0]),
          datasets: [{ label: "Cost basis", data: c.sortedTypes.map(x => x[1]), backgroundColor: "rgba(99,102,241,.7)", borderRadius: 6 }],
        },
        options: {
          plugins: { legend: { display: false }, tooltip: { callbacks: { label: (t) => fmt$(t.parsed.y) } } },
          scales: {
            y: { ticks: { color: th.muted, callback: (v) => "$" + Number(v).toLocaleString() }, grid: { color: th.grid } },
            x: { ticks: { color: th.text }, grid: { display: false } },
          },
        },
      });
    },
  },
  {
    key: "snapshot", label: "Inventory Snapshot", defaultHalf: true, canHalf: true,
    render: c => `
      <div class="panel p-4">
        <h3 class="font-semibold mb-3">Inventory Snapshot</h3>
        <div class="space-y-2 text-sm">
          ${snap("Sealed lots", c.sealed.length)}
          ${snap("Sealed units", fmtN(c.sealedQty))}
          ${snap("Cards / lots", `${fmtN(c.cardsQty)} units / ${c.cards.length} lots`)}
          ${snap("Sealed cost basis", fmt$(c.sealedBasis))}
          ${snap("Cards cost basis", fmt$(c.cardsBasis))}
          ${snap("Mileage YTD", `${fmtN(c.milesYtd)} mi \u00b7 ${fmt$(c.milesDeductionYtd)} deduction`)}
        </div>
      </div>`,
  },
  {
    key: "trend", label: "Monthly Sales Trend (chart)", canHalf: true,
    render: c => `
      <div class="panel p-4">
        <div class="flex items-center justify-between mb-3 flex-wrap gap-2">
          <h3 class="font-semibold">Monthly Sales Trend</h3>
          <div class="text-xs text-[var(--muted)]">
            Last 12 months &middot; Total <b class="${c.trendTotal >= 0 ? 'text-emerald-300' : 'text-rose-300'}">${escapeHtml(fmt$(c.trendTotal))}</b>
            ${c.trendBest > 0 ? ` &middot; Best month <b>${escapeHtml(c.trendLabels[c.trendBestIdx] || "")}</b> at <b class="text-emerald-300">${escapeHtml(fmt$(c.trendBest))}</b>` : ""}
          </div>
        </div>
        <canvas id="chartSalesTrend" height="120"></canvas>
      </div>`,
    after: c => {
      const el = document.getElementById("chartSalesTrend") as HTMLCanvasElement | null;
      if (!el) return;
      const th = chartTheme();
      new Chart(el, {
        type: "bar",
        data: {
          labels: c.trendLabels,
          datasets: [{
            label: "Net Profit",
            data: c.trendValues,
            backgroundColor: c.trendValues.map(v => v >= 0 ? "rgba(34,197,94,.7)" : "rgba(239,68,68,.7)"),
            borderRadius: 4,
          }],
        },
        options: {
          plugins: { legend: { display: false }, tooltip: { callbacks: { label: (t) => fmt$(t.parsed.y) } } },
          scales: {
            y: { ticks: { color: th.muted, callback: (v) => "$" + Number(v).toLocaleString() }, grid: { color: th.grid } },
            x: { ticks: { color: th.text }, grid: { display: false } },
          },
        },
      });
    },
  },
  {
    key: "topCards", label: "Top Cards by FMV", defaultHalf: true, canHalf: true,
    render: c => `
      <div class="panel p-4">
        <h3 class="font-semibold mb-3">Top Cards by FMV</h3>
        <div class="scrollx">
          <table class="t">
            <thead><tr><th></th><th>Card</th><th>Set</th><th>Cond/Grade</th><th class="text-right">Qty</th><th class="text-right">FMV</th></tr></thead>
            <tbody>
              ${c.topCards.length
                ? c.topCards.map(r => {
                  const lotId = escapeHtml(String(r["Card Lot ID"] || ""));
                  const img   = String(r["Image URL"] || "");
                  return `<tr>
                    <td class="text-center">${img ? `<img src="${escapeHtml(img)}" loading="lazy" class="inline-block rounded thumb-hover" style="height:36px; width:auto; max-width:28px" alt="">` : ""}</td>
                    <td><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${lotId}')">${escapeHtml(String(r["Card Name"] || ""))}</a></td>
                    <td class="text-xs text-[var(--muted)]">${escapeHtml(String(r["Set"] || ""))}${r["Card #"] ? ` <span class="opacity-60">#${escapeHtml(String(r["Card #"]))}</span>` : ""}</td>
                    <td class="text-xs">${escapeHtml(String(r["Condition/Grade"] || ""))}</td>
                    <td class="text-right stat-num">${fmtN(r["Qty"])}</td>
                    <td class="text-right stat-num">${fmt$(r["Current FMV"])}</td>
                  </tr>`;
                }).join("")
                : `<tr><td colspan="6" class="text-center text-[var(--muted)] py-4">No cards yet.</td></tr>`}
            </tbody>
          </table>
        </div>
      </div>`,
  },
  {
    key: "recent", label: "Recently Acquired", defaultHalf: true, canHalf: true,
    render: c => `
      <div class="panel p-4">
        <h3 class="font-semibold mb-3">Recently Acquired</h3>
        <div class="scrollx">
          <table class="t">
            <thead><tr><th></th><th>Item</th><th>Kind</th><th>Acq Date</th><th class="text-right">Qty</th><th class="text-right">FMV</th></tr></thead>
            <tbody>
              ${c.recentItems.length
                ? c.recentItems.map(it => `<tr>
                    <td class="text-center">${it.img ? `<img src="${escapeHtml(it.img)}" loading="lazy" class="inline-block rounded thumb-hover" style="height:36px; width:auto; max-width:28px${it._kind === "Sealed" ? "; object-fit:contain" : ""}" alt="">` : ""}</td>
                    <td>
                      <div class="truncate"><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${escapeHtml(it._id)}')">${escapeHtml(it.name)}</a></div>
                      ${it.sub ? `<div class="text-xs text-[var(--muted)] truncate">${escapeHtml(it.sub)}</div>` : ""}
                    </td>
                    <td><span class="pill pill-${it._kind === "Card" ? "violet" : "blue"}">${it._kind}</span></td>
                    <td class="text-xs text-[var(--muted)] whitespace-nowrap">${escapeHtml(fmtDate(it.acq))}</td>
                    <td class="text-right stat-num">${fmtN(it.qty)}</td>
                    <td class="text-right stat-num">${fmt$(it.fmv)}</td>
                  </tr>`).join("")
                : `<tr><td colspan="6" class="text-center text-[var(--muted)] py-4">No items yet \u2014 add a card or sealed lot to get started.</td></tr>`}
            </tbody>
          </table>
        </div>
      </div>`,
  },
  {
    key: "topSealed", label: "Top Sealed Holdings", defaultHalf: true, canHalf: true,
    render: c => `
      <div class="panel p-4">
        <h3 class="font-semibold mb-3">Top Sealed Holdings (by FMV)</h3>
        <div class="scrollx">
          <table class="t">
            <thead><tr><th></th><th>Product</th><th>Set</th><th class="text-right">Qty</th><th class="text-right">FMV</th></tr></thead>
            <tbody>
              ${c.sealed.slice()
                .sort((a, b) => (Number(b["Current FMV"]) || 0) - (Number(a["Current FMV"]) || 0))
                .slice(0, 8)
                .map(r => {
                  const lotId = escapeHtml(String(r["Lot ID"] || ""));
                  const img   = String(r["Image URL"] || "");
                  return `<tr>
                    <td class="text-center">${img ? `<img src="${escapeHtml(img)}" loading="lazy" class="inline-block rounded thumb-hover" style="height:36px; width:auto; max-width:36px; object-fit:contain" alt="">` : ""}</td>
                    <td><a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${lotId}')">${escapeHtml(String(r["Product"] || ""))}</a></td>
                    <td class="text-xs text-[var(--muted)]">${escapeHtml(String(r["Set"] || ""))}</td>
                    <td class="text-right stat-num">${fmtN(r["Qty"])}</td>
                    <td class="text-right stat-num">${fmt$(r["Current FMV"])}</td>
                  </tr>`;
                }).join("") ||
                `<tr><td colspan="5" class="text-center text-[var(--muted)]">No sealed inventory yet.</td></tr>`}
            </tbody>
          </table>
        </div>
      </div>`,
  },
  {
    key: "expenses", label: "Expenses by Category", defaultHalf: true, canHalf: true,
    render: c => `
      <div class="panel p-4">
        <div class="flex items-center justify-between mb-3">
          <h3 class="font-semibold">Expenses by Category</h3>
          <span class="text-xs text-[var(--muted)]">All-time</span>
        </div>
        ${c.expensesSorted.length === 0
          ? `<div class="text-sm text-[var(--muted)]">No expenses logged yet.</div>`
          : c.expensesSorted.map(([k, v]) => {
              const pct = c.expTotal ? Math.round((v / c.expTotal) * 100) : 0;
              return `<div class="mb-2.5">
                <div class="flex justify-between text-sm mb-1">
                  <span>${escapeHtml(k)}</span>
                  <span class="stat-num">${fmt$(v)} <span class="text-[var(--muted)]">(${pct}%)</span></span>
                </div>
                <div class="h-2 bg-[var(--panel-2)] rounded-full overflow-hidden">
                  <div class="h-full bg-gradient-to-r from-indigo-500 to-cyan-400" style="width:${pct}%"></div>
                </div>
              </div>`;
            }).join("")
        }
      </div>`,
  },
  {
    key: "activity", label: "Recent Activity", canHalf: true,
    render: () => `
      <div class="panel p-4">
        <h3 class="font-semibold mb-3">Recent Activity</h3>
        ${renderActivityFeed(20)}
      </div>`,
  },
];

/* ------------------------------------------------------------------ */
/*  Layout persistence (Settings.dashboardLayout via user_prefs)        */
/* ------------------------------------------------------------------ */
type DashLayoutItem = { key: string; hidden?: boolean; half?: boolean };

/** Stored layout merged with the registry: stored order wins, unknown keys
 *  are dropped, widgets added in later versions are appended. Empty stored
 *  layout = registry defaults (the original dashboard). */
function effectiveLayout(): DashLayoutItem[] {
  const stored = (Settings.get().dashboardLayout || []) as DashLayoutItem[];
  const out: DashLayoutItem[] = [];
  for (const it of stored) {
    const w = WIDGETS.find(x => x.key === it.key);
    if (w && !out.some(o => o.key === it.key)) {
      out.push({ key: it.key, hidden: !!it.hidden, half: it.half ?? w.defaultHalf ?? false });
    }
  }
  for (const w of WIDGETS) {
    if (!out.some(o => o.key === w.key)) out.push({ key: w.key, hidden: false, half: w.defaultHalf ?? false });
  }
  return out;
}

function saveLayout(l: DashLayoutItem[]): void {
  Settings.set({ dashboardLayout: l });
}

export function dashToggleWidget(key: string): void {
  const l = effectiveLayout();
  const it = l.find(i => i.key === key); if (!it) return;
  it.hidden = !it.hidden;
  saveLayout(l); render(); openDashboardCustomize();
}
export function dashToggleWidth(key: string): void {
  const l = effectiveLayout();
  const it = l.find(i => i.key === key); if (!it) return;
  it.half = !it.half;
  saveLayout(l); render(); openDashboardCustomize();
}
export function dashMoveWidget(key: string, dir: number): void {
  const l = effectiveLayout();
  const i = l.findIndex(x => x.key === key); if (i < 0) return;
  const j = i + (dir < 0 ? -1 : 1);
  if (j < 0 || j >= l.length) return;
  [l[i], l[j]] = [l[j], l[i]];
  saveLayout(l); render(); openDashboardCustomize();
}
export function dashResetLayout(): void {
  Settings.set({ dashboardLayout: [] });
  render(); openDashboardCustomize();
}

export function openDashboardCustomize(): void {
  const layout = effectiveLayout();
  const rows = layout.map((it, idx) => {
    const w = WIDGETS.find(x => x.key === it.key)!;
    return `
      <div class="flex items-center gap-2 py-1.5 border-b border-[var(--border)] last:border-b-0">
        <label class="flex items-center gap-2 flex-1 cursor-pointer min-w-0">
          <input type="checkbox" ${it.hidden ? "" : "checked"} onchange="dashToggleWidget('${w.key}')">
          <span class="text-sm truncate ${it.hidden ? "text-[var(--muted)]" : ""}">${escapeHtml(w.label)}</span>
        </label>
        ${w.canHalf
          ? `<button class="btn btn-ghost !py-0.5 !px-2 text-xs w-14" onclick="dashToggleWidth('${w.key}')" title="Toggle full / half width (large screens)">${it.half ? "Half" : "Full"}</button>`
          : `<span class="text-xs text-[var(--muted)] w-14 text-center">Full</span>`}
        <button class="btn btn-ghost !py-0.5 !px-1.5 text-xs" ${idx === 0 ? "disabled" : ""} onclick="dashMoveWidget('${w.key}', -1)" title="Move up">${icon("chevron-up", { size: 12 })}</button>
        <button class="btn btn-ghost !py-0.5 !px-1.5 text-xs" ${idx === layout.length - 1 ? "disabled" : ""} onclick="dashMoveWidget('${w.key}', 1)" title="Move down">${icon("chevron-down", { size: 12 })}</button>
      </div>`;
  }).join("");
  openModal({
    title: "Customize Dashboard",
    body: `
      <div class="text-xs text-[var(--muted)] mb-2">Choose which widgets show, reorder them, and pick full or half width (half applies on large screens; everything stacks on mobile). Changes save instantly and sync to your account.</div>
      ${rows}`,
    footer: `
      <button class="btn btn-ghost" onclick="dashResetLayout()" title="Restore the original dashboard layout">Reset to default</button>
      <button class="btn btn-primary" onclick="closeModal()">Done</button>`,
  });
}

/* ------------------------------------------------------------------ */
/*  View                                                                */
/* ------------------------------------------------------------------ */
export function viewDashboard(root: HTMLElement): void {
  const ctx = computeCtx();
  const layout = effectiveLayout();
  const cells: string[] = [];
  const afters: Array<(c: DashCtx) => void> = [];
  for (const item of layout) {
    if (item.hidden) continue;
    const w = WIDGETS.find(x => x.key === item.key);
    if (!w) continue;
    const html = w.render(ctx);
    if (!html) continue;
    const half = w.canHalf ? !!item.half : !!(w.defaultHalf);
    cells.push(`<div class="${half ? "" : "lg:col-span-2"} min-w-0">${html}</div>`);
    if (w.after) afters.push(w.after);
  }
  root.innerHTML = `
    <div id="firstRunHost"></div>
    <div class="flex justify-end mb-2">
      <button class="btn btn-ghost !py-1 !px-2 text-xs" onclick="openDashboardCustomize()" title="Choose, reorder, and resize dashboard widgets">${icon("sliders", { size: 13 })} Customize</button>
    </div>
    ${cells.length
      ? `<div class="grid grid-cols-1 lg:grid-cols-2 gap-4">${cells.join("")}</div>`
      : `<div class="panel p-8 text-center text-[var(--muted)]">All widgets are hidden \u2014 click Customize to bring some back.</div>`}
  `;
  for (const fn of afters) fn(ctx);
}

// Inline-handler bridges
(window as any).openDashboardCustomize = openDashboardCustomize;
(window as any).dashToggleWidget       = dashToggleWidget;
(window as any).dashToggleWidth        = dashToggleWidth;
(window as any).dashMoveWidget         = dashMoveWidget;
(window as any).dashResetLayout        = dashResetLayout;

/* Build a unified chronological feed from sales / purchases / rips / trades / grading / adjustments. */
function renderActivityFeed(limit: number): string {
  type Event = { date: any; icon: string; title: string; id: string; meta: string };
  const events: Event[] = [];

  for (const r of DB.table("sales")) {
    if (String(r["Status"] || "Completed") !== "Completed") continue;
    const items: any[] = Array.isArray(r.Items) ? r.Items : [];
    const summary = items.length === 1
      ? `Sold: ${items[0].Item || items[0]["Lot ID"] || ""}`
      : items.length > 1
        ? `Sold ${items.length} items${r["Buyer"] ? ` to ${r["Buyer"]}` : ""}`
        : `Sold: ${r["Item"] || ""}`;
    events.push({ date: r["Date"], icon: "$", title: summary, id: r["Sale ID"], meta: `Net ${fmt$(r["Net Profit"])}` });
  }
  for (const r of DB.table("purchases")) {
    events.push({
      date: r["Date"],
      icon: "\u21f2",
      title: `Purchase from ${r["Vendor"] || "(unknown)"} \u00b7 ${(r.Items || []).length} item(s)`,
      id: r["Purchase ID"],
      meta: `Paid ${fmt$(r["Total Paid"])}`,
    });
  }
  for (const r of DB.table("rips")) {
    events.push({
      date: r["Date"],
      icon: "\u2702",
      title: `Ripped ${r["Sealed Product"] || ""}`,
      id: r["Rip ID"],
      meta: `Pull FMV ${fmt$(r["Pull FMV"])} \u00b7 EV ${fmt$(r["EV Gain/Loss"])}`,
    });
  }
  for (const r of DB.table("trades")) {
    const tSt  = String(r["Status"] || "Accepted");
    const tIn  = ((r.Incoming as any[]) || []).reduce((s: number, x: any) => s + (Number(x.FMV) || 0) * (Number(x.Qty) || 1), 0);
    const tOut = ((r.Outgoing as any[]) || []).reduce((s: number, x: any) => s + (Number(x.FMV) || 0) * (Number(x.Qty) || 1), 0);
    const tNet = +(tIn - tOut + (Number(r["Cash"]) || 0)).toFixed(2);
    events.push({
      date: r["Date"],
      icon: "\u21c4",
      title: `${tSt === "Accepted" ? "Trade" : "Trade (" + (tSt === "Proposed" ? "Pending" : tSt) + ")"} \u00b7 ${(r.Outgoing || []).length} out / ${(r.Incoming || []).length} in`,
      id: r["Trade ID"],
      meta: `G/L ${fmt$(tNet)}`,
    });
  }
  for (const r of DB.table("grading")) {
    events.push({
      date: r["Date Sent"],
      icon: "\u2605",
      title: `Grading: ${(r.Cards || []).map((c: any) => c["Card Name"]).filter(Boolean).join(", ") || "(no cards)"}`,
      id: r["Submission ID"],
      meta: r["Status"] || "",
    });
  }
  for (const r of DB.table("transactions")) {
    events.push({
      date: r["Date"],
      icon: "\u2261",
      title: `${r["Type"] || "Adjustment"}${r["Description"] ? ` \u2014 ${r["Description"]}` : ""}`,
      id: r["Txn ID"],
      meta: `In ${fmt$(r["Cash In"])} \u00b7 Out ${fmt$(r["Cash Out"])}`,
    });
  }

  events.sort((a, b) => String(b.date || "").localeCompare(String(a.date || "")));
  const sliced = events.slice(0, limit || 20);

  if (!sliced.length) {
    return `<div class="text-sm text-[var(--muted)]">No activity yet \u2014 log a sale, rip a box, or add an adjustment to get started.</div>`;
  }

  return `<div class="space-y-1">${sliced.map(e => {
    const parts = String(e.title || "").split(" \u00b7 ");
    const titleMain = parts.shift() || "";
    const titleSub  = parts.join(" \u00b7 ");
    const meta = e.meta || "";
    // Color the meta only when it leads with a sign-conveying label.
    const signed = /^(G\/L|EV|Net|Profit)\b/.test(meta);
    const moneyCls = signed
      ? (meta.includes("-$") ? "text-rose-300" : "text-emerald-300")
      : "text-[var(--muted)]";
    return `<div class="flex items-center gap-3 py-1.5 border-b border-[var(--border)] last:border-b-0 text-sm">
      <div class="shrink-0 w-6 text-center text-[var(--muted)]">${e.icon}</div>
      <div class="text-xs text-[var(--muted)] shrink-0 w-20">${escapeHtml(fmtDate(e.date))}</div>
      <div class="flex-1 min-w-0">
        <div class="font-medium truncate" title="${escapeHtml(e.title)}">${escapeHtml(titleMain)}</div>
        ${titleSub ? `<div class="text-xs text-[var(--muted)] truncate">${escapeHtml(titleSub)}</div>` : ""}
      </div>
      <div class="shrink-0 hidden md:block text-sm font-medium ${moneyCls} tabular-nums">${escapeHtml(meta)}</div>
      <div class="font-mono text-xs shrink-0 ml-2">${
        e.id
          ? `<a class="text-cyan-300 hover:underline cursor-pointer" onclick="showLotHistory('${escapeHtml(e.id)}')">${escapeHtml(e.id)}</a>`
          : ""
      }</div>
    </div>`;
  }).join("")}</div>`;
}
